// Бланк «КП МосЭл» (прислан РОЗ 25.09.2026) — общие для PDF и Excel тексты,
// колонки и строки таблицы, чтобы два формата не расходились.
//
// Внешний вид — по бланку: шапка «логотип + реквизиты», «Исх. № … от …г.»
// слева и заказчик справа, «Коммерческое предложение», таблица из 7 колонок,
// строка подписанта. Содержание — как у КП СПЕЦМАШЭЛЕКТРО (решение 14.10.2026):
// строки «ВСЕГО с НДС 22%» и «В том числе НДС 22%», условия поставки 1–7,
// «Исполнитель / Ответственный / Телефон / Почта» (общие тексты — kpTerms.ts).
// Подпись и печать не ставим (решение 25.09), подписант печатается текстом.
import type { KpPdfItem } from './generateKpPdf'
import { deliveryTimeByMatrix } from './kpDelivery'
import type { KpEntity } from './kpEntities'
import { kpNdsOf } from './kpTerms'
import type { KpVatView } from './kpVat'

/** Заголовки колонок таблицы — дословно из бланка. */
export const MOSEL_HEADERS = [
  '№',
  'Наименование товара',
  'Предложение',
  'Кол-во',
  'Цена за 1 ед. с НДС',
  'Стоимость с НДС',
  'Срок поставки',
] as const

/** Заголовки в режиме НДС КП: в «НДС сверху» цена и стоимость — без НДС. */
export function moselHeaders(onTop: boolean): readonly string[] {
  return onTop ? MOSEL_HEADERS.map((h) => h.replace('с НДС', 'без НДС')) : MOSEL_HEADERS
}

/** Ширины колонок таблицы бланка, мм (сумма 182,6). */
export const MOSEL_COL_MM = [8.3, 41.7, 42.5, 15, 22.5, 25.2, 27.4] as const

/** Выравнивание ячеек с данными — как в бланке. Шапка таблицы — по центру. */
export const MOSEL_COL_ALIGN: readonly ('left' | 'center')[] = [
  'left',
  'left',
  'left',
  'center',
  'center',
  'center',
  'left',
]

export const MOSEL_TITLE = 'Коммерческое предложение'

/** Цвет реквизитов в шапке бланка (#002060) и линии под шапкой (#1F4D78). */
export const MOSEL_REQ_COLOR = '002060'
export const MOSEL_RULE_COLOR = '1F4D78'

export interface MoselRow {
  no: number
  /** «Наименование товара» — артикул, который прислал заказчик; нет — наш. */
  name: string
  /** «Предложение» — наш артикул и производитель, примечание менеджера — второй строкой. */
  offer: string
  /** Число, если единица — штуки; иначе строка «5 м». */
  qty: number | string
  /** Цена за 1 ед. с НДС, ₽. */
  price: number
  /** Стоимость с НДС, ₽ (цена × кол-во). */
  sum: number
  /** Срок поставки по матрице квоты, как в КП СПЕЦМАШЭЛЕКТРО. */
  delivery: string
}

/** Итоги таблицы: сумма с НДС и НДС в ней, ₽. */
export function moselTotals(rows: MoselRow[]): { total: number; nds: number } {
  const total = rows.reduce((s, r) => s + r.sum, 0)
  return { total, nds: kpNdsOf(total) }
}

/** Единица измерения — штуки (пусто, «шт», «шт.»): тогда её не печатаем. */
export function isPieceUnit(unit: string | null | undefined): boolean {
  const u = unit?.trim() ?? ''
  return u === '' || /^шт\.?$/i.test(u)
}

/** Строки таблицы; `view` — цены и суммы в режиме НДС КП (kpVatView), без него — «С НДС». */
export function buildMoselRows(items: KpPdfItem[], view?: KpVatView): MoselRow[] {
  return items.map((it, idx) => {
    const brand = it.brand?.trim()
    const note = it.note?.trim()
    const offer = `${it.partNumber}${brand ? ` (${brand})` : ''}${note ? `\n${note}` : ''}`
    return {
      no: idx + 1,
      name: it.customerPartNumber?.trim() || it.partNumber,
      offer,
      qty: isPieceUnit(it.unit) ? it.quantity : `${it.quantity} ${it.unit?.trim()}`,
      price: view?.lines[idx]?.price ?? it.priceWithMarkup,
      sum: view?.lines[idx]?.sum ?? it.priceWithMarkup * it.quantity,
      delivery: deliveryTimeByMatrix(it.matrix),
    }
  })
}

/** Подписант: из юрлица, по умолчанию — как в бланке. */
export function moselSigner(entity: KpEntity): { title: string; name: string } {
  return entity.signer ?? { title: 'Генеральный директор', name: 'Рабчук О. С.' }
}

/** Место для подписи от руки между должностью и фамилией. */
export const MOSEL_SIGN_BLANK = '____________________'

/** Дата КП, как в КП СПЕЦМАШЭЛЕКТРО: день скачивания, ДД.ММ.ГГГГ. */
export function kpDateStr(now = new Date()): string {
  return now.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/**
 * «Исх. № … от …г.» — номер заявки (как «КП № …» у СПЕЦМАШЭЛЕКТРО) и дата
 * скачивания. «/1» из образца бланка не ставим, пока РОЗ не скажет, что это.
 */
export function moselOutgoingLine(requestNumber: string | undefined, dateStr: string): string {
  return `Исх. № ${requestNumber?.trim() || '—'} от ${dateStr}г.`
}

/** Деньги с копейками и обычными пробелами (в шрифте PDF нет узких пробелов). */
export function moselMoney(v: number): string {
  return v
    .toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .replace(/[\xA0\u202F]/g, ' ')
}

/** Имя файла, как у КП СПЕЦМАШЭЛЕКТРО: «<клиент> КП <номер> от <дата>.<ext>». */
export function moselFileName(
  clientName: string | null | undefined,
  requestNumber: string | undefined,
  dateStr: string,
  ext: 'pdf' | 'xlsx',
): string {
  const client = clientName
    ?.replace(/[\xA0\u202F]/g, ' ')
    .replace(/[/\\:*?"<>|]/g, '')
    .trim()
  return `${client ? `${client} ` : ''}КП ${requestNumber?.trim() || '—'} от ${dateStr}.${ext}`
}
